/**
 * Sending a command and knowing it arrived.
 *
 * The robot's rule, from docs/architecture.md: nothing is assumed to have happened until the
 * status stream echoes the action's id back. So every command carries one, is resent while
 * that echo has not come, and gives up loudly rather than quietly.
 */
import { json } from "adamo-react";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { actionTopic } from "./topics";
import type { StatusFeed } from "./status";
import { useActionPublisher } from "./publishers";
import { operatorId } from "../state/operatorId";

const RESEND_AFTER_MS = 1000;
const ATTEMPTS = 3;

export type Sender = {
  send: (name: string, extra?: Record<string, unknown>) => void;
  /** Settings while a slider is being dragged: no id, no resend, dropped if they pile up. */
  nudge: (name: string) => void;
  /**
   * Claiming, taking over and letting go. These are answered by the robot's `driver` key
   * rather than by the loop's status, because the link settles them before the loop ever
   * sees them -- so waiting for an action id here would resend for ever.
   */
  own: (name: "claim" | "take_over" | "release_control", extra?: Record<string, unknown>) => void;
};

export function useActions(org: string | null, operator: string, feed: StatusFeed,
                           onLost?: (name: string) => void): Sender {
  const publisher = useActionPublisher(org ? actionTopic(org, operator) : null);
  // In the order they were sent. The robot takes actions off one queue in order, so an
  // acknowledgement of a later one is proof that everything before it was taken too -- which
  // matters because the status carries only the most recent id, and three sliders moved in a
  // second would otherwise all resend.
  const pending = useRef<{ id: string; name: string; bytes: Uint8Array; tries: number; at: number }[]>([]);
  const lost = useRef(onLost);
  lost.current = onLost;

  useEffect(() => {
    const timer = window.setInterval(() => {
      const acked = feed.current()?.action_id ?? null;
      const now = Date.now();
      const at = acked ? pending.current.findIndex((item) => item.id === acked) : -1;
      if (at >= 0) pending.current.splice(0, at + 1);
      const giveUp: string[] = [];
      for (const item of pending.current) {
        if (now - item.at < RESEND_AFTER_MS) continue;
        if (item.tries >= ATTEMPTS) { giveUp.push(item.id); lost.current?.(item.name); continue; }
        item.tries += 1;
        item.at = now;
        publisher.send(item.bytes);   // the robot drops a repeat of an id it already took
      }
      if (giveUp.length) pending.current = pending.current.filter((item) => !giveUp.includes(item.id));
    }, 200);
    return () => window.clearInterval(timer);
  }, [feed, publisher]);

  const send = useCallback((name: string, extra: Record<string, unknown> = {}) => {
    const id = crypto.randomUUID();
    const bytes = json.encode({ type: "action", name, request_id: id, operator_id: operatorId(), ...extra });
    pending.current.push({ id, name, bytes, tries: 0, at: Date.now() });
    publisher.send(bytes);
  }, [publisher]);

  const nudge = useCallback((name: string) => {
    publisher.send(json.encode({ type: "action", name, operator_id: operatorId() }));
  }, [publisher]);

  const own = useCallback((name: string, extra: Record<string, unknown> = {}) => {
    publisher.send(json.encode({ type: "action", name, operator_id: operatorId(), ...extra }));
  }, [publisher]);

  return useMemo(() => ({ send, nudge, own }), [send, nudge, own]);
}
